/**
 * useProjectRole(projectId) — canonical project-scoped role resolver.
 *
 * Consolidates the patchy inline `user?.role === 'ADMIN'` and
 * `user?.global_role === 'superadmin'` checks scattered across ~14
 * components into a single hook that returns the caller's per-project
 * role + a permissions bag consumers can read directly.
 *
 * Source of truth is the JWT payload already embedded in `useAuth().user`
 * (see `backend/routers/auth_router.py:82-86, 121-126, 312-317` — the
 * token carries `global_role` + `project_roles: {[project_id]: {role,
 * write_modules, ...}}`). No network fetch is required for the base
 * resolution — the hook returns synchronously in the common case.
 *
 * Reuse policy (Phase 2.5 dispatch, 2026-02): does NOT introduce any new
 * roles. Whitelists the 10 department roles from
 * `backend/services/rbac_config.py::ROLE_MODULE_MAP` plus the coarse
 * global roles `user | producer | superadmin`.
 *
 * @param {string} projectId Optional. When omitted, the hook resolves
 *   the caller's global permissions only (superadmin / producer bypass).
 *
 * @returns {{
 *   role: string|null,
 *   globalRole: string,
 *   writeModules: string[],
 *   permissions: {
 *     canWrite: boolean,
 *     canEdit:  boolean,
 *     canDelete:boolean,
 *     canManageTeam: boolean,
 *     isArtDept:   boolean,
 *     isCostume:   boolean,
 *     isCasting:   boolean,
 *     isProduction:boolean,
 *     isReadOnly:  boolean,
 *     isSuperadmin:boolean,
 *     isProducer:  boolean,
 *   },
 *   isLoading: boolean,
 *   error: Error|null,
 * }}
 *
 * @example
 *   const { permissions } = useProjectRole(projectId);
 *   if (!permissions.canWrite) return <ReadOnlyBanner />;
 */
import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';

// Kept in-sync with backend/services/rbac_config.py::ROLE_MODULE_MAP.
// If a new dept role is added on the backend, update this whitelist too.
const _DEPT_ROLES = new Set([
  'director', 'production', 'scheduler', 'art_department', 'costume',
  'casting', 'continuity', 'dop', 'vfx', 'sound',
]);

// Roles that grant write access to their own department module.
const _WRITE_ROLES = new Set([
  'director', 'production', 'scheduler', 'art_department', 'costume',
  'casting', 'continuity', 'dop', 'vfx', 'sound',
]);

export function useProjectRole(projectId) {
  const { user } = useAuth();

  return useMemo(() => {
    const empty = {
      role: null,
      globalRole: 'user',
      writeModules: [],
      permissions: {
        canWrite: false, canEdit: false, canDelete: false,
        canManageTeam: false,
        isArtDept: false, isCostume: false, isCasting: false, isProduction: false,
        isReadOnly: true, isSuperadmin: false, isProducer: false,
      },
      isLoading: false,
      error: null,
    };

    if (!user) return { ...empty, isLoading: true };

    const globalRole = user.global_role || 'user';
    const isSuperadmin = globalRole === 'superadmin';
    const isProducer   = globalRole === 'producer';

    // Superadmin + producer bypass ALL per-project role checks.
    if (isSuperadmin || isProducer) {
      return {
        role: isSuperadmin ? 'superadmin' : 'producer',
        globalRole,
        writeModules: [],  // implicitly all
        permissions: {
          canWrite: true, canEdit: true, canDelete: true,
          canManageTeam: true,
          isArtDept: true, isCostume: true, isCasting: true, isProduction: true,
          isReadOnly: false,
          isSuperadmin, isProducer,
        },
        isLoading: false,
        error: null,
      };
    }

    // No projectId supplied → coarse global check only.
    if (!projectId) return { ...empty, globalRole };

    const entry = (user.project_roles || {})[projectId] || null;
    const role = entry?.role || null;
    const writeModules = entry?.write_modules || [];

    if (!role) {
      // Not a member of this project → read-only if member; nothing if not.
      // The endpoint layer enforces the actual gate; UI just hides affordances.
      return { ...empty, globalRole };
    }

    const isDept = _DEPT_ROLES.has(role);
    const canWrite = _WRITE_ROLES.has(role);
    // Fine-grained categories consumers can key off:
    const permissions = {
      canWrite,
      canEdit: canWrite,
      canDelete: canWrite,
      canManageTeam: role === 'production',
      isArtDept:    role === 'art_department',
      isCostume:    role === 'costume',
      isCasting:    role === 'casting',
      isProduction: role === 'production',
      isReadOnly:   isDept ? !canWrite : true,
      isSuperadmin: false,
      isProducer:   false,
    };

    return {
      role,
      globalRole,
      writeModules,
      permissions,
      isLoading: false,
      error: null,
    };
  }, [user, projectId]);
}

export default useProjectRole;
